import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { getApiBaseUrl } from "../../config";

export const proofOfPaymentApi = createApi({
  reducerPath: "proofOfPaymentApi",
  baseQuery: fetchBaseQuery({
    baseUrl: getApiBaseUrl(),
    credentials: "include",
    prepareHeaders: (headers, { getState }) => {
      const user = getState().user?.currentUser;
      const token = getState().companyProfile?.token;
      if (token) {
        headers.set("x-auth-token", token);
      } else if (user?.id) {
        headers.set("userid", user.id);
      }
      return headers;
    },
  }),
  tagTypes: ["ProofOfPayment"],
  endpoints: (builder) => ({
    getProofOfPayments: builder.query({
      async queryFn(
        { pageSize = 100, nextToken = undefined, page = 1, limit = 1000 } = {},
        queryApi,
        extraOptions,
        baseQuery
      ) {
        try {
          const result = await baseQuery({
            url: "payments",
            params: { pageSize, ...(nextToken ? { nextToken } : {}), page, limit },
          });
          return result;
        } catch (error) {
          console.error("Error fetching MVDs:", error);
          // Return empty data if the API fails
          return {
            data: {
              data: [],
            },
          };
        }
      },
      providesTags: ["ProofOfPayment"],
    }),
    getAllProofOfPayments: builder.query({
      async queryFn({ searchQuery = "", createdFrom = "", createdTo = "", page = 1, limit = 100 } = {}, queryApi, extraOptions, baseQuery) {
        const result = await baseQuery({
          url: "payments",
          params: {
            ...(searchQuery && { search: searchQuery }),
            ...(createdFrom && { createdFrom }),
            ...(createdTo && { createdTo }),
            page,
            limit
          },
        });
        return result;
      },
      providesTags: (result, error, arg) => {
        // Handle case where arg might be undefined
        const { searchQuery = "", createdFrom = "", createdTo = "", page = 1 } = arg || {};
        return searchQuery || createdFrom || createdTo || page > 1
          ? [{ type: "ProofOfPayment", id: `SEARCH-${searchQuery}-${createdFrom}-${createdTo}-${page}` }]
          : ["ProofOfPayment"];
      },
    }),
    getProofOfPaymentById: builder.query({
      query: (id) => `/payments/${id}`,
      providesTags: (result, error, id) => [{ type: "ProofOfPayment", id }],
    }),
    addProofOfPayment: builder.mutation({
      query: (payment) => ({
        url: "/payments",
        method: "POST",
        body: payment,
      }),
      invalidatesTags: ["ProofOfPayment"],
    }),
    // Update proof of payment
    updateProofOfPayment: builder.mutation({
      async queryFn({ id, formData }, queryApi, extraOptions, baseQuery) {
        const result = await baseQuery({
          url: `payments/${id}`,
          method: "PUT",
          body: formData,
        });
        return result;
      },
      invalidatesTags: (result, error, { id }) => [
        { type: "ProofOfPayment", id },
        "ProofOfPayment",
      ],
      // query: ({ id, formData }) => ({
      //   url: `/payments/${id}`,
      //   method: 'PUT',
      //   body: formData,
      // }),
      // invalidatesTags: (result, error, { id }) => [{ type: 'ProofOfPayment', id }],
    }),
    deleteProofOfPayment: builder.mutation({
      query: (id) => ({
        url: `/payments/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["ProofOfPayment"],
    }),
    getRecordsByCompanyAndType: builder.query({
      query: ({ companyId, paymentType }) => `/payments/records/${companyId}/${paymentType}`,
    }),
    calculatePaymentDetails: builder.query({
      query: ({ recordId, paymentType, editingPaymentId }) => ({
        url: `/payments/calculate/${recordId}/${paymentType}`,
        params: editingPaymentId ? { editingPaymentId } : {},
      }),
    }),
  }),
});

export const {
  useGetProofOfPaymentsQuery,
  useGetAllProofOfPaymentsQuery,
  useLazyGetAllProofOfPaymentsQuery,
  useGetProofOfPaymentByIdQuery,
  useAddProofOfPaymentMutation,
  useUpdateProofOfPaymentMutation,
  useDeleteProofOfPaymentMutation,
  useGetRecordsByCompanyAndTypeQuery,
  useCalculatePaymentDetailsQuery,
} = proofOfPaymentApi;
